import React, { useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import API from '../lib/api';

/**
 * Portfolio.jsx — Vista consolidada del patrimonio del cliente.
 *
 * Sprint 3 (branch feature/dual-balance-v2). Aislado de producción.
 *
 * Muestra:
 *  - Patrimonio total (suma de COP + USD + acciones + DeFi + fondos)
 *  - Donut de distribución por categoría
 *  - Lista de posiciones (Alpaca + Kamino + fondos colombianos cuando aplique)
 *  - Tesis del Cerebro 2.0 (filosofía + para ti si + anti-fit + compañero de viaje)
 *
 * Fuentes de datos:
 *  - /user/getDashboardInfo → balance_cop, balance_usdc
 *  - Alpaca: posiciones de acciones USA (sandbox por ahora)
 *  - Kamino: posiciones de DeFi
 *  - Progresión: NOT YET — placeholder hasta que se firme el partnership
 */
export default function Portfolio() {
  const snap = useSnapshot(state);
  const [loading, setLoading] = useState(true);
  const [balanceCop, setBalanceCop] = useState(0);
  const [balanceUsdc, setBalanceUsdc] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(4140);
  const [stockPositions, setStockPositions] = useState([]);
  const [kaminoPositions, setKaminoPositions] = useState([]);
  const [fundsPositions] = useState([]); // placeholder — Progresión partnership pendiente

  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      try {
        // 1. Saldos COP + USD del wallet del cliente
        const dash = await API.get('user/getDashboardInfo');
        if (!cancelled) {
          setBalanceCop(dash.data?.data?.balance_cop ?? 0);
          setBalanceUsdc(dash.data?.data?.balance_usdc ?? dash.data?.data?.balance ?? 0);
        }

        // 2. Tasa de cambio actual
        try {
          const rateRes = await API.get('/transaction/get_exchange_rate');
          if (!cancelled && rateRes.data?.rate) setExchangeRate(rateRes.data.rate);
        } catch { /* fallback rate 4140 */ }

        // 3. Posiciones DeFi Kamino (si existen)
        try {
          const kRes = await API.get('/rewards/kamino/position');
          if (!cancelled && kRes.data?.data) {
            const positions = Array.isArray(kRes.data.data) ? kRes.data.data : [kRes.data.data];
            setKaminoPositions(positions.filter((p) => p && p.amount));
          }
        } catch { /* no Kamino positions */ }

        // 4. Posiciones Alpaca (acciones USA) — solo si la integración está activa
        //    Se intentará cargar; si falla (no hay cuenta), queda en []
        try {
          const investRes = await API.get('/user/getInvestmentPositions');
          if (!cancelled && Array.isArray(investRes.data?.data)) {
            setStockPositions(investRes.data.data);
          }
        } catch { /* sin posiciones Alpaca */ }
      } catch (err) {
        console.error('[Portfolio] load error:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAll();
    return () => { cancelled = true; };
  }, []);

  // Cálculo de patrimonio total normalizado a COP (para el donut + total)
  const totalStocksUsd = stockPositions.reduce((s, p) => s + parseFloat(p.market_value || 0), 0);
  const totalKaminoUsd = kaminoPositions.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const totalFundsCop = fundsPositions.reduce((s, p) => s + parseFloat(p.value_cop || 0), 0);

  const totalCop = balanceCop + totalFundsCop;
  const totalUsd = balanceUsdc + totalStocksUsd + totalKaminoUsd;
  const totalPatrimonioCop = totalCop + totalUsd * exchangeRate;
  const totalPatrimonioUsd = totalPatrimonioCop / exchangeRate;

  // Porcentajes para el donut (sobre patrimonio total en COP)
  const pct = (cop) => totalPatrimonioCop > 0 ? (cop / totalPatrimonioCop) * 100 : 0;
  const pctPesos = pct(balanceCop);
  const pctDolares = pct(balanceUsdc * exchangeRate);
  const pctAcciones = pct(totalStocksUsd * exchangeRate);
  const pctDeFi = pct(totalKaminoUsd * exchangeRate);
  const pctFondos = pct(totalFundsCop);

  // Conic gradient stops (en orden)
  const conic = `conic-gradient(
    #C9A961 0% ${pctPesos}%,
    #86EFAC ${pctPesos}% ${pctPesos + pctDolares}%,
    #0A2540 ${pctPesos + pctDolares}% ${pctPesos + pctDolares + pctAcciones}%,
    #7C3AED ${pctPesos + pctDolares + pctAcciones}% ${pctPesos + pctDolares + pctAcciones + pctDeFi}%,
    #FCD34D ${pctPesos + pctDolares + pctAcciones + pctDeFi}% 100%
  )`;

  const fmtCop = (n) => `$ ${Math.round(n).toLocaleString('es-CO')}`;
  const fmtUsd = (n) => `$ ${parseFloat(n).toFixed(2)}`;

  if (loading) {
    return (
      <div className="p-10 text-center text-[#6B7280]">Cargando tu portafolio...</div>
    );
  }

  return (
    <div className="px-6 py-6 sm:px-10 sm:py-8 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xl font-semibold text-[#181D27]">Mi Portafolio</p>
          <p className="text-sm text-[#535862]">Todo tu patrimonio en un solo lugar</p>
        </div>
      </div>

      {/* Patrimonio total — hero card */}
      <div
        className="rounded-[20px] p-6 text-white"
        style={{ background: 'linear-gradient(135deg, #0A2540, #1A3A6C)' }}
      >
        <p className="text-xs uppercase tracking-wider opacity-70 font-semibold">Patrimonio total</p>
        <p className="text-3xl sm:text-4xl font-bold mt-1">{fmtCop(totalPatrimonioCop)} <span className="text-lg opacity-80">COP</span></p>
        <p className="text-sm opacity-90 mt-1">≈ {fmtUsd(totalPatrimonioUsd)} USD</p>
      </div>

      {/* Donut + Leyenda */}
      <div className="bg-white rounded-[20px] border border-[#E6EBF7] p-6">
        <p className="text-base font-semibold text-[#181D27] mb-4">Distribución</p>

        <div className="flex flex-col sm:flex-row gap-6 items-center">
          {/* Donut visual usando conic-gradient */}
          <div className="relative flex-shrink-0">
            <div
              className="w-40 h-40 rounded-full"
              style={{ background: conic }}
            />
            <div className="absolute inset-8 bg-white rounded-full flex flex-col items-center justify-center text-center">
              <p className="text-[10px] uppercase text-[#9CA3AF] tracking-wider">Total</p>
              <p className="text-sm font-bold text-[#0A2540]">{fmtCop(totalPatrimonioCop)}</p>
            </div>
          </div>

          {/* Leyenda */}
          <div className="flex-1 w-full space-y-2 text-sm">
            <LegendRow color="#C9A961" label="🇨🇴 Pesos líquidos" value={fmtCop(balanceCop)} pct={pctPesos} />
            <LegendRow color="#86EFAC" label="🌎 Dólares líquidos" value={fmtUsd(balanceUsdc)} pct={pctDolares} />
            <LegendRow color="#0A2540" label="📈 Acciones USA" value={fmtUsd(totalStocksUsd)} pct={pctAcciones} />
            <LegendRow color="#7C3AED" label="🪴 DeFi (Kamino)" value={fmtUsd(totalKaminoUsd)} pct={pctDeFi} />
            <LegendRow color="#FCD34D" label="🏛️ Fondos Colombia" value={fmtCop(totalFundsCop)} pct={pctFondos} />
          </div>
        </div>
      </div>

      {/* Posiciones detalladas */}
      <div className="bg-white rounded-[20px] border border-[#E6EBF7] p-6">
        <p className="text-base font-semibold text-[#181D27] mb-4">Tus posiciones</p>

        {stockPositions.length === 0 && kaminoPositions.length === 0 && fundsPositions.length === 0 ? (
          <div className="text-center py-8 text-sm text-[#9CA3AF]">
            Aún no tienes posiciones de inversión activas.<br />
            Convierte tus pesos a dólares y empieza a invertir.
          </div>
        ) : (
          <div className="space-y-3">
            {stockPositions.map((p) => (
              <PositionRow
                key={`stock-${p.symbol}`}
                icon="📈"
                ticker={p.symbol}
                name={p.symbol}
                detail={`${parseFloat(p.qty).toFixed(4)} acciones`}
                value={fmtUsd(parseFloat(p.market_value))}
                changePct={parseFloat(p.unrealized_plpc) * 100}
              />
            ))}
            {kaminoPositions.map((p, i) => (
              <PositionRow
                key={`kamino-${i}`}
                icon="🪴"
                ticker="USDC"
                name="USDC en Kamino"
                detail="Rendimiento DeFi · Solana"
                value={fmtUsd(p.amount)}
                changePct={parseFloat(p.apy || 8.2)}
                changeLabel="APY"
                bg="#F5F3FF"
                border="#C4B5FD"
              />
            ))}
            {fundsPositions.map((p, i) => (
              <PositionRow
                key={`fund-${i}`}
                icon="🏛️"
                ticker={p.ticker}
                name={p.name}
                detail={`${p.units} unidades · Progresión`}
                value={fmtCop(p.value_cop)}
                changePct={p.apy_pct}
                changeLabel="E.A."
                bg="#FFFBF0"
                border="#FCD34D"
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer note */}
      <p className="text-xs text-[#9CA3AF] text-center">
        Sprint 3 · branch feature/dual-balance-v2 · activación cuando firmen Alpaca + Progresión
      </p>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------

function LegendRow({ color, label, value, pct }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <div className="flex items-center gap-2 min-w-0">
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="text-sm text-[#181D27] truncate">{label}</span>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="text-sm font-semibold text-[#181D27]">{value}</span>
        <span className="text-xs text-[#9CA3AF] w-12 text-right">{pct.toFixed(0)}%</span>
      </div>
    </div>
  );
}

function PositionRow({ icon, ticker, name, detail, value, changePct, changeLabel = '', bg = 'white', border = '#E6EBF7' }) {
  const isPositive = changePct >= 0;
  return (
    <div
      className="rounded-[14px] border p-3 flex items-center gap-3"
      style={{ background: bg, borderColor: border }}
    >
      <div className="w-10 h-10 rounded-[10px] bg-[#F9FAFB] flex items-center justify-center font-bold text-xs text-[#0A2540] flex-shrink-0">
        {ticker.slice(0, 4)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <span>{icon}</span>
          <span className="text-sm font-semibold text-[#181D27] truncate">{name}</span>
        </div>
        <div className="text-xs text-[#6B7280]">{detail}</div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-sm font-bold text-[#181D27]">{value}</div>
        <div className={`text-xs font-semibold ${isPositive ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
          {isPositive ? '+' : ''}{parseFloat(changePct).toFixed(2)}% {changeLabel}
        </div>
      </div>
    </div>
  );
}
